import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrNotificationLink } from '../utils/hrNotificationLink';
import { videoVisitAlertLink, openVideoVisitWindow } from '../utils/videoVisitAlertLink';
import { authorizationPaceAlertLink } from '../utils/authorizationPaceAlertLink';
import {
  BellIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useNotificationsContext } from '../context/NotificationsContext.jsx';

/**
 * Unified notifications dropdown — reads from persisted Notification table.
 * Supports all notification categories (visit, auth, credentials, reviews, etc.)
 */
export default function AlertsDropdown() {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    refreshNotifications
  } = useNotificationsContext();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Open dropdown and fetch notifications
  const handleToggleDropdown = () => {
    if (!isOpen) {
      fetchNotifications({ limit: 15, unreadOnly: true });
    }
    setIsOpen(!isOpen);
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.readAt) {
        await markAsRead(notification.id);
      }
      setIsOpen(false);

      // Navigate based on notification data
      const data = notification.data || {};
      const videoLink = videoVisitAlertLink(notification);
      const paceLink = authorizationPaceAlertLink(notification);
      if (paceLink) {
        navigate(paceLink.href);
      } else if (hrNotificationLink(notification)) {
        navigate(hrNotificationLink(notification));
      } else if (videoLink) {
        if (videoLink.newWindow) openVideoVisitWindow(videoLink.href);
        else navigate(videoLink.href);
      } else if (data.visitId) {
        navigate(`/admin/visits/${data.visitId}`);
      } else if (data.authorizationId) {
        navigate('/admin/authorizations');
      } else if (data.treatmentPlanId) {
        navigate('/admin/treatment-plans');
      } else if (data.documentId && notification.templateId?.startsWith('review_')) {
        navigate('/admin/clinical');
      } else if (data.issueId && notification.templateId?.startsWith('support_ticket_')) {
        navigate(`/admin/issues?ticket=${encodeURIComponent(data.issueId)}`);
      } else {
        navigate('/admin/alerts');
      }
    } catch (err) {
      console.error('Failed to handle notification click:', err);
    }
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get icon based on templateId prefix
  const getNotificationIcon = (notification) => {
    const templateId = notification.templateId || '';
    if (templateId.startsWith('visit_missing_checkin')) return '🕐';
    if (templateId.startsWith('visit_missing_checkout')) return '🕑';
    if (templateId.startsWith('visit_missing_signature')) return '✍️';
    if (templateId.startsWith('visit_geofence')) return '📍';
    if (templateId.startsWith('authorization_')) return '🛡️';
    if (templateId.startsWith('treatment_plan_')) return '📋';
    if (templateId.startsWith('initial_contact_')) return '⏱️';
    if (templateId.startsWith('credential_')) return '🏅';
    if (templateId.startsWith('document_')) return '📄';
    if (templateId.startsWith('schedule_')) return '📅';
    if (templateId.startsWith('review_revision')) return '✏️';
    if (templateId.startsWith('review_approved')) return '✅';
    if (templateId.startsWith('review_rejected')) return '❌';
    if (templateId.startsWith('review_pending')) return '📝';
    if (templateId.startsWith('compliance_')) return '⚖️';
    if (templateId.startsWith('billing_')) return '💰';
    return '🔔';
  };

  // Get category label from templateId
  const getCategoryLabel = (notification) => {
    const templateId = notification.templateId || '';
    if (templateId.startsWith('visit_')) return 'Visit Compliance';
    if (templateId.startsWith('authorization_')) return 'Authorization';
    if (templateId.startsWith('treatment_plan_')) return 'Treatment Plan';
    if (templateId.startsWith('initial_contact_')) return 'Initial Contact';
    if (templateId.startsWith('credential_')) return 'Credential';
    if (templateId.startsWith('document_')) return 'Document Review';
    if (templateId.startsWith('schedule_')) return 'Schedule';
    if (templateId.startsWith('review_')) return 'Document Review';
    if (templateId.startsWith('compliance_')) return 'Compliance';
    if (templateId.startsWith('billing_')) return 'Billing';
    return 'System';
  };

  // Get priority badge color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300';
      case 'high': return 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300';
      case 'normal': return 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300';
      case 'low': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
    }
  };

  // Filter to unread only for the dropdown
  const unreadNotifications = notifications
    .filter(n => !n.readAt)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 10);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={handleToggleDropdown}
        className="relative p-2 rounded-lg hover:bg-white dark:bg-gray-800/20 transition-all duration-200 group"
        aria-label={`Alerts ${unreadCount > 0 ? `(${unreadCount})` : ''}`}
      >
        <BellIcon className="h-5 w-5 text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:text-white" />

        {/* Badge */}
        {unreadCount > 0 && !isOpen && (
          <span
            aria-live="polite"
            aria-label={`${unreadCount > 99 ? '99+' : unreadCount} unread alerts`}
            className="absolute -top-1 -right-1 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full shadow-sm min-w-[18px] flex items-center justify-center"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-1 w-96 max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 shadow-lg rounded-lg border z-50 max-h-[min(34rem,80vh)] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-2 py-1.5 border-b bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-white text-xs">Notifications</h3>
            <div className="flex items-center gap-1">
              {error && (
                <button
                  onClick={refreshNotifications}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-300 text-xs"
                  title="Retry"
                >
                  ↻
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="px-2 py-1.5 bg-red-50 border-b">
              <div className="flex items-center gap-1 text-red-700 dark:text-red-300">
                <ExclamationTriangleIcon className="h-3 w-3" />
                <span className="text-xs">Failed to load: {error}</span>
              </div>
              <button
                onClick={refreshNotifications}
                className="mt-0.5 text-xs text-red-600 hover:text-red-800 dark:text-red-300 underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && !error && (
            <div className="px-2 py-1.5">
              <div className="flex items-center gap-1.5">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600"></div>
                <span className="text-gray-600 dark:text-gray-400 text-xs">Loading notifications...</span>
              </div>
            </div>
          )}

          {/* Notifications List */}
          {!loading && !error && (
            <div className="max-h-[min(28rem,70vh)] overflow-y-auto">
              {unreadNotifications.length === 0 ? (
                <div className="px-2 py-3 text-center">
                  <BellIcon className="h-6 w-6 text-gray-300 mx-auto mb-1" />
                  <p className="text-gray-500 text-xs">No new notifications</p>
                  <p className="text-xs text-gray-400 mt-0.5">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y">
                  {unreadNotifications.map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className="w-full px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 transition-colors focus:bg-gray-50 focus:outline-none"
                    >
                      <div className="flex items-start gap-1.5">
                        <span className="text-xs flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-1">
                            <p className="font-medium text-gray-900 dark:text-white text-xs leading-tight line-clamp-2">
                              {notification.title}
                            </p>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              <span className={`px-1 py-0.5 text-xs rounded font-medium leading-none ${getPriorityColor(notification.priority)}`}>
                                {(notification.priority || 'normal').charAt(0).toUpperCase()}
                              </span>
                              <div className="w-1 h-1 bg-blue-600 rounded-full flex-shrink-0"></div>
                            </div>
                          </div>

                          {/* Category */}
                          <p className="text-xs text-blue-600 font-medium mt-0.5 leading-tight line-clamp-1">
                            {getCategoryLabel(notification)}
                          </p>

                          <div className="mt-0.5 space-y-0">
                            {notification.content && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 leading-tight line-clamp-2">
                                {notification.content}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 leading-tight">
                              {formatTime(notification.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="border-t">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/admin/alerts');
              }}
              className="w-full px-2 py-1.5 text-center text-orange-600 hover:bg-orange-50 text-xs font-medium transition-colors"
            >
              View All Notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
